import { useSelector } from 'react-redux';
import { getActiveLocaleSelector, getIsLoggedInSelector } from '@americanexpress/travel-selectors';
import { useConditionalFeature, locales } from '@americanexpress/conditional-features';
import { useMembershipDomainFetchye } from '@americanexpress/consumer-travel-base';
import { capitaliseFirstLetterUtil } from '../utils/capitaliseFirstLetterUtil';
import { hasVacationRentalsRule } from '../constants/featureRulesConstants';
import { MODULE_NAME } from '../constants/modules';
import { useTravelerData } from './useTravelerData';
import { getSelectedAccountFromTravelerV2 } from '../utils/membershipDataUtils';
import { MEMBERSHIP_QUERY } from '../graphql/membership';

export const useMembershipData = () => {
  const isLoggedIn = useSelector(getIsLoggedInSelector);
  const activeLocale = useSelector(getActiveLocaleSelector);
  const isUsMarket = activeLocale === locales.EN_US;
  const hasVacationRentals = useConditionalFeature(hasVacationRentalsRule);

  const fetcherOpts = {
    reporting: {
      moduleID: MODULE_NAME,
      componentID: 'useMembershipData',
      fetch: {
        activeLocale,
      },
    },
    locale: activeLocale,
  };

  const {
    data: membershipData,
    isLoading: membershipIsLoading,
    error: membershipError,
  } = useMembershipDomainFetchye(
    { query: MEMBERSHIP_QUERY },
    {
      defer: !isLoggedIn || !isUsMarket || !hasVacationRentals,
    },
    fetcherOpts
  );

  const { travelerData, travelerIsLoading, travelerError } = useTravelerData();

  const selectedAccount = travelerData && getSelectedAccountFromTravelerV2(travelerData);

  const data = {
    ...membershipData && isUsMarket && { ...membershipData },
    ...travelerData && !isUsMarket && {
      data: {
        membership: [{
          demographics: {
            isTopCard: true,
            homeCountryLocale: travelerData?.card?.locale,
            product: {
              productFamily: travelerData?.card?.type,
              cardArtCdnLink: travelerData?.card?.cardArtCdnLink,
              productId: travelerData?.card?.cardIdCode,
            },
            displayAccountNumber: selectedAccount?.displayAccountNumber,
          },
          loyalty: {
            pointsBalance: selectedAccount?.points,
          },
          travelCredits: selectedAccount?.travelCredit?.map((credit) => ({
            awardName: credit?.name,
            awardDescription: credit?.marketingText,
            landingPage: credit?.landingPage,
            callToActionText: credit?.callToActionText,
            expiryDate: credit?.expiry,
          })),
        }],
      },
    },
  };

  return {
    data,
    isLoading: membershipIsLoading || travelerIsLoading,
    error: membershipError || travelerError,
  };
};

export const useFirstName = () => {
  const { data: membershipData } = useMembershipData();
  let firstName = null;

  if (Array.isArray(membershipData?.data?.membership)) {
    firstName = membershipData?.data?.membership[0]?.demographics?.firstName;
  }

  firstName = firstName && capitaliseFirstLetterUtil(firstName);
  return firstName;
};

export const useAccountToken = () => {
  const { data: membershipData } = useMembershipData();
  let accountToken = null;

  if (Array.isArray(membershipData?.data?.membership)) {
    accountToken = membershipData?.data?.membership[0]?.accountToken;
  }

  return accountToken;
};
